/**
 * Landing page sub-components — extracted from Landing.jsx during the
 * Phase 2 refactor. Each component renders one slot of the marketing
 * page and is independently testable.
 *
 * Inter-component dependencies:
 *   • LangSwitcher / SectionHeader / BentoCard / FaqRow / FooterCol /
 *     Metric — leaves with no dependencies on other landing components.
 *   • RoiCalculator / PricingSection — consume COPY only.
 *   • FloatingProductMontage — uses FrameMaps + FrameAudit + FrameKanban
 *     defined below.
 *   • ExcelVsAgenteLeadDramatic — uses Metric + VS_COPY (local).
 *   • ScrollytellingRail — uses MiniWorkflowVideo + SCROLLY_STEPS (local).
 *   • CompareMobileStack — uses VS_COPY (re-imported from this file).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, ChevronDown, MapPin, Sparkles, Workflow,
  Globe, Search, Inbox,
  BarChart3, FileSpreadsheet, TrendingDown, Rocket, X as XIcon, Clock,
} from "lucide-react";
import COPY from "./copy";
import { AVATARS, LANGS, fadeUp } from "./constants";


export function LangSwitcher({ lang, setLang, dark = false }) {
  const [open, setOpen] = useState(false);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-lang-switcher]")) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  return (
    <div data-lang-switcher data-testid="landing-lang-switcher" className="relative">
      {/* Mobile + tablet: compact pill — globe + active code + chevron. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="landing-lang-current"
        className="md:hidden inline-flex items-center gap-1 h-9 px-2.5 rounded-full border text-[11.5px] font-bold tracking-wider transition-colors"
        style={{
          borderColor: dark ? "rgba(255,255,255,0.15)" : "rgba(132,204,22,0.25)",
          backgroundColor: dark ? "rgba(255,255,255,0.05)" : "#1E293B",
          color: "#FFFFFF",
        }}>
        <Globe className="w-3.5 h-3.5" style={{ color: "#84CC16" }} />
        <span>{current.flag}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Desktop (md+): keep the inline 3-pill toggle for instant switch. */}
      <div className="hidden md:inline-flex items-center gap-0.5 p-1 rounded-full border"
        style={{ borderColor: dark ? "rgba(255,255,255,0.15)" : "rgba(132,204,22,0.18)", backgroundColor: dark ? "rgba(255,255,255,0.05)" : "#1E293B" }}>
        {LANGS.map((l) => {
          const active = l.code === lang;
          return (
            <button key={l.code} type="button" onClick={() => setLang(l.code)}
              data-testid={`landing-lang-${l.code}`}
              className="px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider transition-all"
              style={{
                backgroundColor: active ? "#84CC16" : "transparent",
                color: active ? "#0F172A" : "rgba(255,255,255,0.65)",
              }}>
              <Globe className="inline w-3 h-3 mr-0.5" />{l.flag}
            </button>
          );
        })}
      </div>

      {/* Mobile dropdown panel */}
      {open && (
        <div role="listbox" data-testid="landing-lang-dropdown"
          className="md:hidden absolute right-0 top-full mt-1.5 z-50 min-w-[120px] rounded-xl border shadow-2xl overflow-hidden"
          style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.25)" }}>
          {LANGS.map((l) => {
            const active = l.code === lang;
            return (
              <button key={l.code} type="button"
                onClick={() => { setLang(l.code); setOpen(false); }}
                role="option" aria-selected={active}
                data-testid={`landing-lang-${l.code}`}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-[12.5px] font-bold tracking-wider transition-colors"
                style={{
                  backgroundColor: active ? "rgba(132,204,22,0.12)" : "transparent",
                  color: active ? "#84CC16" : "rgba(255,255,255,0.78)",
                }}>
                <span className="inline-flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" /> {l.flag}
                </span>
                {active && <Check className="w-3.5 h-3.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SectionHeader({ eyebrow, title, sub, align = "left" }) {
  const alignClass = align === "center" ? "text-center mx-auto" : "";
  return (
    <div className={`max-w-3xl ${alignClass}`}>
      {eyebrow && <p className="font-mono-tech text-[10.5px] uppercase tracking-[0.28em]" style={{ color: "#A1A1AA" }}>{eyebrow}</p>}
      <h2 className="font-display mt-3 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight" style={{ color: "#FFFFFF" }}>{title}</h2>
      {sub && <p className="mt-4 text-[16px] md:text-[18px] leading-relaxed" style={{ color: "#A1A1AA" }}>{sub}</p>}
    </div>
  );
}

export function BentoCard({ data, className, feature = false, testid }) {
  const Icon = data.icon;
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
      data-testid={testid}
      className={`relative group border rounded-xl p-7 md:p-8 hover:shadow-xl hover:-translate-y-1 transition-all overflow-hidden ${className}`}
      style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.18)" }}>
      {feature && (<div className="absolute inset-0 -z-10 opacity-50 pointer-events-none" style={{ backgroundImage: "radial-gradient(at 90% 0%, rgba(132,204,22,0.12), transparent 60%)" }} />)}
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ backgroundColor: "#0A1233", color: "#84CC16" }}>
          <Icon className="w-5 h-5" />
        </div>
        {data.kicker && (<span className="text-[10px] font-bold uppercase tracking-[0.16em] px-2 py-1 rounded" style={{ backgroundColor: "#84CC16", color: "#FFFFFF" }}>{data.kicker}</span>)}
      </div>
      <h3 className={`font-display mt-6 font-bold tracking-tight ${feature ? "text-2xl md:text-3xl" : "text-xl"}`} style={{ color: "#FFFFFF" }}>{data.t}</h3>
      <p className={`mt-2 leading-relaxed ${feature ? "text-[15px] max-w-md" : "text-[13.5px]"}`} style={{ color: "#A1A1AA" }}>{data.d}</p>
    </motion.div>
  );
}

export function FaqRow({ idx, q, a, open, onToggle }) {
  return (
    <div data-testid={`landing-faq-item-${idx}`} className="border-b" style={{ borderColor: "rgba(132,204,22,0.18)" }}>
      <button type="button" onClick={onToggle}
        data-testid={`landing-faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-6 py-5 text-left group">
        <span className="font-display text-[17px] md:text-[19px] font-semibold transition-colors" style={{ color: "#FFFFFF" }}>{q}</span>
        <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} style={{ color: "#A1A1AA" }} />
      </button>
      <div className={`grid transition-all duration-500 ease-out ${open ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden"><p className="text-[14.5px] leading-relaxed max-w-2xl" style={{ color: "#A1A1AA" }}>{a}</p></div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// ROI Calculator (interactive · client-only) + Pricing
// ----------------------------------------------------------------------------

const ROI_COPY = {
  pt: {
    eyebrow: "Calculadora de ROI",
    title: "Quanto seu time pode gerar com o AgenteLead Pro?",
    sub: "Insira seu nicho, cidade e capacidade de atendimento. Calculamos uma estimativa realista de leads, oportunidades qualificadas e receita potencial.",
    niche: "Nicho", city: "Cidade", ticket: "Ticket médio (US$)", capacity: "Mensagens/mês",
    leads: "Leads gerados",
    opps: "Oportunidades qualificadas",
    revenue: "Receita estimada/mês",
    micro: "Cálculo conservador: 1 busca = 25 leads, 22% qualifica, 8% fecha. Ajuste seu próprio benchmark dentro da plataforma.",
    cta: "Começar FREE TRIAL",
  },
  en: {
    eyebrow: "ROI Calculator",
    title: "How much can your team generate with AgenteLead Pro?",
    sub: "Type in your niche, city and outreach capacity. We compute a realistic estimate of leads, qualified opportunities and revenue potential.",
    niche: "Niche", city: "City", ticket: "Avg deal value (US$)", capacity: "Outreach/month",
    leads: "Leads generated",
    opps: "Qualified opportunities",
    revenue: "Estimated revenue/mo",
    micro: "Conservative math: 1 search = 25 leads, 22% qualify, 8% close. Tune your own benchmarks inside the platform.",
    cta: "Start FREE TRIAL",
  },
  es: {
    eyebrow: "Calculadora de ROI",
    title: "¿Cuánto puede generar tu equipo con AgenteLead Pro?",
    sub: "Ingresa tu nicho, ciudad y capacidad de outreach. Calculamos una estimación realista de leads, oportunidades calificadas y potencial de ingresos.",
    niche: "Nicho", city: "Ciudad", ticket: "Ticket promedio (US$)", capacity: "Mensajes/mes",
    leads: "Leads generados",
    opps: "Oportunidades calificadas",
    revenue: "Ingresos estimados/mes",
    micro: "Cálculo conservador: 1 búsqueda = 25 leads, 22% califica, 8% cierra. Ajusta tus benchmarks dentro de la plataforma.",
    cta: "Empezar FREE TRIAL",
  },
};

export function RoiCalculator({ lang }) {
  const t = ROI_COPY[lang] || ROI_COPY.en;
  const [niche, setNiche] = useState("Roofing");
  const [city, setCity] = useState("Miami, FL");
  const [ticket, setTicket] = useState(2500);
  const [capacity, setCapacity] = useState(800);
  // Conservative funnel: capacity → 25 leads/search assumed average prospecting
  // velocity, 22% qualifies, 8% closes.
  const leads = Math.round((capacity || 0) * 1);          // 1 lead per outreach
  const opps  = Math.round(leads * 0.22);
  const wins  = Math.round(leads * 0.08);
  const revenue = Math.round(wins * (Number(ticket) || 0));
  const fmt = (n) => new Intl.NumberFormat(lang === "pt" ? "pt-BR" : lang === "es" ? "es-ES" : "en-US").format(n || 0);
  const fmtUSD = (n) => `$${fmt(n)}`;

  return (
    <section id="roi" data-testid="roi-calculator" className="py-20 lg:py-28" style={{ backgroundColor: "#160C2D", borderTop: "1px solid rgba(132,204,22,0.15)", borderBottom: "1px solid rgba(132,204,22,0.15)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <p className="font-mono-tech text-[10.5px] uppercase tracking-[0.28em]" style={{ color: "#84CC16" }}>{t.eyebrow}</p>
          <h2 className="font-display mt-3 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight" style={{ color: "#FFFFFF" }}>{t.title}</h2>
          <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "#A1A1AA" }}>{t.sub}</p>
          <p className="mt-6 text-[12px] leading-relaxed" style={{ color: "#71717A" }}>{t.micro}</p>
        </div>
        <div className="lg:col-span-7 rounded-2xl border p-7 lg:p-9" style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.25)", boxShadow: "0 12px 48px -16px rgba(132,204,22,0.18)" }}>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { lbl: t.niche, val: niche, set: setNiche, testid: "roi-niche", type: "text" },
              { lbl: t.city, val: city, set: setCity, testid: "roi-city", type: "text" },
              { lbl: t.ticket, val: ticket, set: (v) => setTicket(Number(v)), testid: "roi-ticket", type: "number" },
              { lbl: t.capacity, val: capacity, set: (v) => setCapacity(Number(v)), testid: "roi-capacity", type: "number" },
            ].map((f) => (
              <label key={f.lbl} className="block">
                <div className="text-[11px] font-mono-tech uppercase tracking-[0.18em] mb-1.5" style={{ color: "#A1A1AA" }}>{f.lbl}</div>
                <input type={f.type} value={f.val} onChange={(e) => f.set(e.target.value)}
                  data-testid={f.testid}
                  className="w-full h-11 px-3.5 rounded-lg outline-none text-[14px]"
                  style={{ backgroundColor: "#0F172A", border: "1px solid rgba(132,204,22,0.25)", color: "#FFFFFF" }} />
              </label>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { l: t.leads, v: fmt(leads), testid: "roi-out-leads", color: "#FFFFFF" },
              { l: t.opps, v: fmt(opps), testid: "roi-out-opps", color: "#10B981" },
              { l: t.revenue, v: fmtUSD(revenue), testid: "roi-out-revenue", color: "#84CC16" },
            ].map((s) => (
              <div key={s.l} data-testid={s.testid} className="rounded-xl p-4 text-center" style={{ backgroundColor: "#0F172A", border: "1px solid rgba(132,204,22,0.18)" }}>
                <div className="text-[10.5px] font-mono-tech uppercase tracking-[0.18em]" style={{ color: "#A1A1AA" }}>{s.l}</div>
                <div className="font-display font-black text-2xl md:text-3xl mt-1.5 tabular-nums" style={{ color: s.color }}>{s.v}</div>
              </div>
            ))}
          </div>
          <Link to="/signup" data-testid="roi-cta"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 font-bold text-[13.5px] px-5 py-3.5 rounded-md transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: "#84CC16", color: "#0F172A" }}>
            {t.cta}<ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

const PRICING_COPY = {
  pt: {
    eyebrow: "Planos",
    title: "Comece grátis. Escale quando seu pipeline pedir.",
    sub: "14 dias FREE TRIAL em qualquer plano. Sem cartão de crédito. Cancele a qualquer momento.",
    most: "Mais Popular",
    cta: "Começar FREE TRIAL",
    tiers: [
      { name: "Entry", price: "$49", per: "/mês", desc: "Perfeito pra solo SDR ou consultor.", features: ["1.000 leads/mês", "Google Maps Places API", "Kanban CRM básico", "Auditorias IA limitadas"] },
      { name: "Pro", price: "$149", per: "/mês", desc: "Para times que prospectam todo dia.", features: ["5.000 leads/mês", "Tudo do Entry +", "Auditorias IA ilimitadas", "Outreach Sequencer", "AI Reply Inbox", "5 usuários"], popular: true },
      { name: "Premium", price: "$399", per: "/mês", desc: "Agências e operações multi-cliente.", features: ["Leads ilimitados", "Tudo do Pro +", "Social Prospecting (IG/LI/YT)", "Public Works · SAM.gov", "Multi-tenant", "Usuários ilimitados", "Suporte prioritário"] },
    ],
  },
  en: {
    eyebrow: "Plans",
    title: "Start free. Scale when your pipeline asks for it.",
    sub: "14-day FREE TRIAL on any plan. No credit card. Cancel anytime.",
    most: "Most Popular",
    cta: "Start FREE TRIAL",
    tiers: [
      { name: "Entry", price: "$49", per: "/mo", desc: "Solo SDR or consultant.", features: ["1,000 leads/mo", "Google Maps Places API", "Basic Kanban CRM", "Limited AI audits"] },
      { name: "Pro", price: "$149", per: "/mo", desc: "For teams prospecting daily.", features: ["5,000 leads/mo", "Everything in Entry +", "Unlimited AI audits", "Outreach Sequencer", "AI Reply Inbox", "5 seats"], popular: true },
      { name: "Premium", price: "$399", per: "/mo", desc: "Agencies and multi-client ops.", features: ["Unlimited leads", "Everything in Pro +", "Social Prospecting (IG/LI/YT)", "Public Works · SAM.gov", "Multi-tenant", "Unlimited seats", "Priority support"] },
    ],
  },
  es: {
    eyebrow: "Planes",
    title: "Empieza gratis. Escala cuando tu pipeline lo pida.",
    sub: "14 días FREE TRIAL en cualquier plan. Sin tarjeta de crédito. Cancela cuando quieras.",
    most: "Más Popular",
    cta: "Empezar FREE TRIAL",
    tiers: [
      { name: "Entry", price: "$49", per: "/mes", desc: "Solo SDR o consultor.", features: ["1.000 leads/mes", "Google Maps Places API", "Kanban CRM básico", "Auditorías IA limitadas"] },
      { name: "Pro", price: "$149", per: "/mes", desc: "Para equipos que prospectan a diario.", features: ["5.000 leads/mes", "Todo de Entry +", "Auditorías IA ilimitadas", "Outreach Sequencer", "AI Reply Inbox", "5 usuarios"], popular: true },
      { name: "Premium", price: "$399", per: "/mes", desc: "Agencias y operaciones multi-cliente.", features: ["Leads ilimitados", "Todo de Pro +", "Social Prospecting (IG/LI/YT)", "Public Works · SAM.gov", "Multi-tenant", "Usuarios ilimitados", "Soporte prioritario"] },
    ],
  },
};

export function PricingSection({ lang }) {
  const t = PRICING_COPY[lang] || PRICING_COPY.en;
  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 lg:py-28" style={{ backgroundColor: "#0F172A" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <SectionHeader eyebrow={t.eyebrow} title={t.title} sub={t.sub} />
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {t.tiers.map((tier) => (
            <div key={tier.name} data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
              className={`relative rounded-2xl border p-7 flex flex-col transition-all hover:-translate-y-1 ${tier.popular ? "lg:-translate-y-2" : ""}`}
              style={{
                backgroundColor: "#1E293B",
                borderColor: tier.popular ? "#84CC16" : "rgba(132,204,22,0.18)",
                borderWidth: tier.popular ? 2 : 1,
                boxShadow: tier.popular ? "0 16px 60px -20px rgba(132,204,22,0.45)" : "none",
              }}>
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.18em]"
                  style={{ backgroundColor: "#84CC16", color: "#0F172A" }}
                  data-testid="pricing-most-popular">
                  ★ {t.most}
                </div>
              )}
              <h3 className="font-display text-2xl font-black" style={{ color: "#FFFFFF" }}>{tier.name}</h3>
              <p className="mt-1.5 text-[13px]" style={{ color: "#A1A1AA" }}>{tier.desc}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-5xl font-black" style={{ color: tier.popular ? "#84CC16" : "#FFFFFF" }}>{tier.price}</span>
                <span className="text-[13px]" style={{ color: "#A1A1AA" }}>{tier.per}</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13.5px]" style={{ color: "#FFFFFF" }}>
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: tier.popular ? "#84CC16" : "#10B981" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup" data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                className="mt-7 w-full inline-flex items-center justify-center gap-2 font-bold text-[13px] py-3 rounded-md transition-all"
                style={{
                  backgroundColor: tier.popular ? "#84CC16" : "transparent",
                  color: tier.popular ? "#0F172A" : "#84CC16",
                  border: tier.popular ? "none" : "1.5px solid #84CC16",
                }}>
                {t.cta}<ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FooterCol({ title, items }) {
  // Map known link labels to public-page routes; unmapped labels remain
  // anchor links to top of page.
  const ROUTE_MAP = {
    // Product
    "Como funciona": "#how", "How it works": "#how", "Cómo funciona": "#how",
    "Para quem é": "#solutions", "Who it's for": "#solutions", "Para quién es": "#solutions",
    "FAQ": "#faq",
    "Acessar plataforma": "/login", "Open platform": "/login", "Abrir plataforma": "/login",
    // Company
    "Sobre": "/about", "About": "/about", "Nosotros": "/about",
    "Contato": "/contact", "Contact": "/contact", "Contacto": "/contact",
    "Carreiras": "/careers", "Careers": "/careers", "Carreras": "/careers",
    // Legal
    "Privacidade": "/privacy", "Privacy": "/privacy", "Privacidad": "/privacy",
    "Termos": "/terms", "Terms": "/terms", "Términos": "/terms",
    "Cookies": "/cookies",
  };
  return (
    <div className="lg:col-span-2">
      <h4 className="font-mono-tech text-[10.5px] uppercase tracking-[0.22em] mb-4" style={{ color: "#84CC16" }}>{title}</h4>
      <ul className="space-y-2.5">
        {items.map((it) => {
          const route = ROUTE_MAP[it] || "#top";
          const isInternal = route.startsWith("/");
          const slug = it.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          return (
            <li key={it}>
              {isInternal ? (
                <Link to={route} data-testid={`footer-link-${slug}`} className="text-[13.5px] transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }}>{it}</Link>
              ) : (
                <a href={route} data-testid={`footer-link-${slug}`} className="text-[13.5px] transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }}>{it}</a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function FloatingProductMontage({ lang }) {
  const L = {
    pt: { hot: "QUENTE", audit: "AUDITORIA IA · GPT", auditCopy: "Local SEO 64/100 · sem schema · 12 reviews em 6 meses · Oportunidade: vídeo no GMB.", reviewed: "2 SDRs revisaram", won: "WON · 3", reply: "REPLY RATE" },
    en: { hot: "HOT", audit: "AI AUDIT · GPT", auditCopy: "Local SEO 64/100 · no schema · 12 reviews in 6 months · Opportunity: GMB video.", reviewed: "2 SDRs reviewed", won: "WON · 3", reply: "REPLY RATE" },
    es: { hot: "HOT", audit: "AI AUDIT · GPT", auditCopy: "SEO local 64/100 · sin schema · 12 reseñas en 6 meses · Oportunidad: video en GMB.", reviewed: "2 SDRs revisaron", won: "WON · 3", reply: "TASA DE RESPUESTA" },
  };
  const t = L[lang] || L.en;
  return (
    <div className="relative w-full h-full">
      <motion.div initial={{ opacity: 0, y: 30, rotate: -3 }} animate={{ opacity: 1, y: 0, rotate: -3 }} transition={{ duration: 0.7, delay: 0.1 }}
        className="absolute top-2 left-0 w-[280px] rounded-xl shadow-xl p-4 z-20 border"
        style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.18)" }}
        data-testid="hero-card-search">
        <div className="flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "#A1A1AA" }}>
          <MapPin className="w-3 h-3" style={{ color: "#84CC16" }} /> Roofing · Miami, FL
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-[15px]" style={{ color: "#FFFFFF" }}>Coastal Roofing Co.</div>
            <div className="text-[11px] mt-0.5" style={{ color: "#A1A1AA" }}>2300 Henderson Dr · ★ 4.8</div>
          </div>
          <span className="text-[10px] font-bold px-2 py-1 rounded" style={{ backgroundColor: "#FEE2E2", color: "#DC2626" }}>{t.hot}</span>
        </div>
        <div className="mt-3 h-1.5 rounded overflow-hidden" style={{ backgroundColor: "#F1F5F9" }}>
          <div className="h-full w-[82%]" style={{ background: "linear-gradient(to right, #F97316, #84CC16)" }} />
        </div>
        <div className="mt-2 text-[10px] tabular-nums font-mono-tech" style={{ color: "#A1A1AA" }}>SCORE 82/100</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 30, rotate: 4 }} animate={{ opacity: 1, y: 0, rotate: 4 }} transition={{ duration: 0.7, delay: 0.25 }}
        className="absolute bottom-12 right-0 w-[300px] rounded-xl shadow-2xl p-5 z-30 border"
        style={{ backgroundColor: "#0A1233", color: "#FFFFFF", borderColor: "rgba(132,204,22,0.35)" }}
        data-testid="hero-card-audit">
        <div className="flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "#E5C572" }}>
          <Sparkles className="w-3 h-3" /> {t.audit}
        </div>
        <div className="mt-3 text-[13px] leading-snug" style={{ color: "rgba(255,255,255,0.85)" }}>{t.auditCopy}</div>
        <div className="mt-4 flex items-center gap-2">
          <div className="flex -space-x-2">{AVATARS.slice(0, 2).map((a) => (<img key={a} src={a} alt="" className="w-6 h-6 rounded-full ring-2 object-cover" style={{ "--tw-ring-color": "#0A1233" }} />))}</div>
          <span className="text-[10.5px]" style={{ color: "rgba(255,255,255,0.6)" }}>{t.reviewed}</span>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
        className="absolute top-32 right-4 w-[180px] rounded-xl shadow-lg p-3 z-10 border"
        style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.18)" }}
        data-testid="hero-card-kanban">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded inline-flex" style={{ color: "#059669", backgroundColor: "#D1FAE5" }}>
          <Workflow className="w-2.5 h-2.5" /> {t.won}
        </div>
        {[{n:"Acme HVAC",v:"$12k"},{n:"Sunshine Plb.",v:"$8k"},{n:"Bayview",v:"$5k"}].map((d) => (
          <div key={d.n} className="mt-2 p-2 rounded-md" style={{ backgroundColor: "#160C2D" }}>
            <div className="text-[11px] font-semibold" style={{ color: "#FFFFFF" }}>{d.n}</div>
            <div className="text-[10px] tabular-nums" style={{ color: "#A1A1AA" }}>{d.v}</div>
          </div>
        ))}
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.55 }}
        className="absolute bottom-0 left-12 rounded-xl shadow-xl px-5 py-4 z-30 border"
        style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.18)" }}
        data-testid="hero-card-stats">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5" style={{ color: "#10B981" }} />
          <div>
            <div className="font-display text-2xl font-black tabular-nums leading-none" style={{ color: "#FFFFFF" }}>32%</div>
            <div className="text-[10px] mt-0.5 uppercase tracking-wider font-semibold" style={{ color: "#A1A1AA" }}>{t.reply}</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}


// ----------------------------------------------------------------------------
// Iter56 — DRAMATIC Excel-Pain ↔ Automation-Pleasure section
// ----------------------------------------------------------------------------

const VS_COPY = {
  pt: {
    eyebrow: "Pare de queimar dinheiro",
    title: "A planilha é cara. Muito mais cara do que você acha.",
    sub: "Cada hora copiando do Google Maps é uma hora que seu time NÃO está fechando. Faça as contas:",
    stamp_pain: "Dor da planilha",
    stamp_win: "Vitória do AgenteLead",
    pain_metric: "4h 47min", pain_metric_lbl: "perdidas/SDR/dia",
    win_metric: "< 2min", win_metric_lbl: "do mapa ao Kanban",
    delta_label: "Aceleração",
    delta_value: "143×",
    save_band: "Você reembolsa o trial em 2 leads.",
    cta: "Aposentar minha planilha agora",
    micro: "14 dias FREE TRIAL · sem cartão · cancele a qualquer momento",
  },
  en: {
    eyebrow: "Stop bleeding hours",
    title: "The spreadsheet is expensive. Way more than you think.",
    sub: "Every hour copying from Google Maps is an hour your team is NOT closing. Do the math:",
    stamp_pain: "Spreadsheet pain",
    stamp_win: "AgenteLead win",
    pain_metric: "4h 47min", pain_metric_lbl: "lost/SDR/day",
    win_metric: "< 2min", win_metric_lbl: "from map to Kanban",
    delta_label: "Speed-up",
    delta_value: "143×",
    save_band: "You pay back the trial in 2 closed leads.",
    cta: "Retire my spreadsheet now",
    micro: "14-day FREE TRIAL · no card · cancel anytime",
  },
  es: {
    eyebrow: "Deja de quemar horas",
    title: "La planilla es cara. Mucho más de lo que crees.",
    sub: "Cada hora copiando de Google Maps es una hora que tu equipo NO está cerrando. Saca cuentas:",
    stamp_pain: "Dolor de planilla",
    stamp_win: "Victoria AgenteLead",
    pain_metric: "4h 47min", pain_metric_lbl: "perdidas/SDR/día",
    win_metric: "< 2min", win_metric_lbl: "del mapa al Kanban",
    delta_label: "Aceleración",
    delta_value: "143×",
    save_band: "Recuperás el trial cerrando 2 leads.",
    cta: "Jubilar mi planilla ahora",
    micro: "14 días FREE TRIAL · sin tarjeta · cancela cuando quieras",
  },
};

export function ExcelVsAgenteLeadDramatic({ c, lang }) {
  const t = VS_COPY[lang] || VS_COPY.en;
  // Staggered entrance for the heading, stat strip, and side-by-side cards.
  const reveal = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
  };
  return (
    <motion.section data-testid="feature-vs-excel" className="mt-12 lg:mt-16"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.18 }}
      variants={{ show: { transition: { staggerChildren: 0.12 } } }}>
      {/* Section heading */}
      <motion.div className="max-w-3xl mb-8" variants={reveal}>
        <p className="font-mono-tech text-[10.5px] uppercase tracking-[0.28em]" style={{ color: "#EF4444" }}>
          {t.eyebrow}
        </p>
        <h3 className="font-display mt-2.5 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]" style={{ color: "#FFFFFF" }}>
          {t.title}
        </h3>
        <p className="mt-3 text-[14.5px] leading-relaxed max-w-2xl" style={{ color: "#A1A1AA" }}>
          {t.sub}
        </p>
      </motion.div>

      {/* Stat strip — pain hours vs gain seconds + multiplier */}
      <motion.div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8" variants={reveal}>
        <Metric tone="pain" icon={Clock}
          value={t.pain_metric} label={t.pain_metric_lbl}
          testid="vs-metric-pain" />
        <Metric tone="delta" icon={TrendingDown}
          value={t.delta_value} label={t.delta_label}
          testid="vs-metric-delta" />
        <Metric tone="win" icon={Rocket}
          value={t.win_metric} label={t.win_metric_lbl}
          testid="vs-metric-win" />
      </motion.div>

      {/* Side-by-side dramatic cards */}
      <motion.div className="relative grid md:grid-cols-2 gap-4 md:gap-0"
        variants={{
          hidden: { opacity: 0 },
          show: { opacity: 1, transition: { staggerChildren: 0.18, delayChildren: 0.05 } },
        }}>
        {/* PAIN COLUMN — blood red, sharp, decay */}
        <motion.div data-testid="vs-pain-card"
          variants={{
            hidden: { opacity: 0, x: -40 },
            show: { opacity: 1, x: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
          }}
          className="rounded-2xl md:rounded-r-none p-6 md:p-8 lg:p-10 relative overflow-hidden border-2 md:border-r-0"
          style={{
            borderColor: "rgba(239,68,68,0.45)",
            background: "linear-gradient(155deg, #2A0A0A 0%, #4A1010 60%, #5C1212 100%)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
          }}>
          {/* decay grain */}
          <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-overlay"
            style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(239,68,68,0.22), transparent 55%), radial-gradient(circle at 80% 80%, rgba(127,29,29,0.35), transparent 50%)" }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md font-mono-tech text-[10.5px] uppercase tracking-[0.22em] font-bold mb-5"
              style={{ backgroundColor: "rgba(239,68,68,0.18)", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)" }}>
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>{t.stamp_pain}</span>
            </div>
            <ul className="space-y-3.5">
              {c.features.vs_excel.map((v) => (
                <li key={v.bad} className="text-[13.5px] md:text-[14.5px] leading-snug flex items-start gap-3 font-semibold" style={{ color: "#FECACA" }}>
                  <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center"
                    style={{ backgroundColor: "rgba(239,68,68,0.25)", border: "1px solid rgba(239,68,68,0.5)" }}>
                    <XIcon className="w-3 h-3" style={{ color: "#FCA5A5" }} strokeWidth={3} />
                  </span>
                  <span className="line-through opacity-90 decoration-[#EF4444]/60 decoration-2">{v.bad}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* VS coin — desktop only, sits on the seam */}
        <motion.div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none"
          aria-hidden="true"
          variants={{
            hidden: { opacity: 0, scale: 0.4, rotate: -180 },
            show: { opacity: 1, scale: 1, rotate: 0,
                    transition: { duration: 0.7, ease: [0.34, 1.56, 0.64, 1], delay: 0.4 } },
          }}>
          <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full font-display font-black flex items-center justify-center text-[16px] lg:text-[19px] tracking-tighter"
            style={{
              background: "linear-gradient(135deg, #E5C572 0%, #84CC16 60%, #9A7A2E 100%)",
              color: "#0F172A",
              boxShadow: "0 12px 40px -10px rgba(132,204,22,0.7), inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 4px rgba(0,0,0,0.25)",
              border: "2px solid rgba(255,255,255,0.15)",
            }}>VS</div>
        </motion.div>

        {/* WIN COLUMN — premium dark + gold accent */}
        <motion.div data-testid="vs-win-card"
          variants={{
            hidden: { opacity: 0, x: 40 },
            show: { opacity: 1, x: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
          }}
          className="rounded-2xl md:rounded-l-none p-6 md:p-8 lg:p-10 relative overflow-hidden border-2"
          style={{
            borderColor: "rgba(132,204,22,0.55)",
            background: "linear-gradient(135deg, #0E1F1A 0%, #103D2E 50%, #134E3F 100%)",
            boxShadow: "0 18px 60px -22px rgba(16,185,129,0.4), inset 0 1px 0 rgba(255,255,255,0.05)",
          }}>
          {/* gold dust */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-[320px] h-[320px] rounded-full blur-3xl opacity-50"
            style={{ background: "radial-gradient(circle, rgba(132,204,22,0.45), transparent 60%)" }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md font-mono-tech text-[10.5px] uppercase tracking-[0.22em] font-bold mb-5"
              style={{ backgroundColor: "rgba(132,204,22,0.2)", color: "#E5C572", border: "1px solid rgba(132,204,22,0.5)" }}>
              <Rocket className="w-3.5 h-3.5" />
              <span>{t.stamp_win}</span>
            </div>
            <ul className="space-y-3.5">
              {c.features.vs_excel.map((v) => (
                <li key={v.good} className="text-[13.5px] md:text-[14.5px] leading-snug flex items-start gap-3 font-bold" style={{ color: "#FFFFFF" }}>
                  <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center"
                    style={{ backgroundColor: "#10B981", boxShadow: "0 0 14px rgba(16,185,129,0.55)" }}>
                    <Check className="w-3 h-3" style={{ color: "#0F172A" }} strokeWidth={4} />
                  </span>
                  <span>{v.good}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-lg p-3 font-mono-tech text-[11.5px] tracking-wide flex items-center gap-2"
              style={{ backgroundColor: "rgba(132,204,22,0.12)", color: "#E5C572", border: "1px dashed rgba(132,204,22,0.45)" }}>
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <span>{t.save_band}</span>
            </div>

            {/* Iter57 — 6s loop "proof video" simulating cursor moving from
                Google Maps search → AI audit → Kanban card landing. Pure SVG
                + framer-motion: zero MP4 weight, zero scrubbing tax. */}
            <MiniWorkflowVideo lang={lang} />

            <Link to="/signup" data-testid="feature-vs-excel-cta"
              className="mt-5 group inline-flex items-center gap-2 font-extrabold text-[13.5px] px-6 py-3.5 rounded-md transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: "#84CC16", color: "#0F172A",
                boxShadow: "0 12px 40px -12px rgba(132,204,22,0.7)",
              }}>
              {t.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <p className="mt-3 text-[11px]" style={{ color: "rgba(255,255,255,0.55)" }}>{t.micro}</p>
          </div>
        </motion.div>
      </motion.div>
    </motion.section>
  );
}

export function Metric({ tone, icon: Icon, value, label, testid }) {
  const palette = tone === "pain" ? {
    bg: "linear-gradient(135deg, rgba(239,68,68,0.10) 0%, rgba(127,29,29,0.18) 100%)",
    border: "rgba(239,68,68,0.35)", valueColor: "#FCA5A5",
    labelColor: "rgba(252,165,165,0.65)", iconColor: "#EF4444",
  } : tone === "win" ? {
    bg: "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(6,95,70,0.20) 100%)",
    border: "rgba(16,185,129,0.45)", valueColor: "#6EE7B7",
    labelColor: "rgba(110,231,183,0.7)", iconColor: "#10B981",
  } : {
    bg: "linear-gradient(135deg, rgba(132,204,22,0.12) 0%, rgba(120,90,30,0.22) 100%)",
    border: "rgba(132,204,22,0.55)", valueColor: "#E5C572",
    labelColor: "rgba(229,197,114,0.7)", iconColor: "#84CC16",
  };
  return (
    <div data-testid={testid}
      className="rounded-xl border p-3 sm:p-4 lg:p-5 flex flex-col gap-1 lg:gap-1.5"
      style={{ background: palette.bg, borderColor: palette.border }}>
      <Icon className="w-4 h-4 lg:w-5 lg:h-5" style={{ color: palette.iconColor }} />
      <div className="font-display font-black tabular-nums leading-none text-[clamp(1.5rem,4.5vw,2.5rem)] tracking-tighter mt-1"
        style={{ color: palette.valueColor }}>
        {value}
      </div>
      <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.14em] font-bold leading-tight"
        style={{ color: palette.labelColor }}>
        {label}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Iter57 — Scrollytelling rail (hero) + Mini workflow "video" (WIN card)
// ----------------------------------------------------------------------------

const SCROLLY_STEPS = {
  pt: ["Pesquise", "Audite com IA", "Feche no Kanban"],
  en: ["Search", "AI Audit", "Close on Kanban"],
  es: ["Buscar", "Auditar con IA", "Cerrar en Kanban"],
};

export function ScrollytellingRail({ progress, lang }) {
  const labels = SCROLLY_STEPS[lang] || SCROLLY_STEPS.en;
  const thresholds = [0.18, 0.50, 0.82];
  const activeIdx = thresholds.reduce((acc, t, i) => (progress >= t ? i : acc), -1);
  return (
    <div data-testid="hero-scrollytelling" aria-hidden="true"
      className="hidden md:block absolute top-2 left-0 right-0 z-30 pointer-events-none">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.22em]"
          style={{ color: "rgba(255,255,255,0.45)" }}>
          {labels.map((lbl, i) => {
            const done = i <= activeIdx;
            return (
              <div key={lbl} className="flex items-center gap-2">
                <span data-testid={`scrolly-step-${i}`}
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full font-black tabular-nums text-[10px] transition-colors"
                  style={{
                    backgroundColor: done ? "#84CC16" : "transparent",
                    color: done ? "#0F172A" : "rgba(255,255,255,0.5)",
                    border: done ? "none" : "1px solid rgba(255,255,255,0.18)",
                  }}>
                  {done ? "✓" : i + 1}
                </span>
                <span style={{ color: done ? "#E5C572" : "rgba(255,255,255,0.45)" }}>{lbl}</span>
                {i < labels.length - 1 && (
                  <span className="hidden lg:block h-px w-12 transition-colors"
                    style={{ backgroundColor: i < activeIdx ? "#84CC16" : "rgba(255,255,255,0.18)" }} />
                )}
              </div>
            );
          })}
          <div className="flex-1" />
          <div className="hidden lg:flex items-center gap-2 text-[#A1A1AA]">
            <span className="text-[#10B981]">●</span>
            {lang === "pt" ? "ao vivo · 143× mais rápido"
             : lang === "es" ? "en vivo · 143× más rápido"
             : "live · 143× faster"}
          </div>
        </div>
      </div>
    </div>
  );
}

export function MiniWorkflowVideo({ lang }) {
  const [frame, setFrame] = useState(0);
  const FRAMES = 3;
  const FRAME_MS = 2000;

  useEffect(() => {
    const id = setInterval(() => setFrame((f) => (f + 1) % FRAMES), FRAME_MS);
    return () => clearInterval(id);
  }, []);

  const captions = {
    pt: ["1. Pesquise no Google Maps", "2. IA audita em <90s", "3. Pronto no Kanban"],
    en: ["1. Search Google Maps", "2. AI audits in <90s", "3. Lands in Kanban"],
    es: ["1. Busca en Google Maps", "2. IA audita en <90s", "3. Listo en Kanban"],
  };
  const labels = captions[lang] || captions.en;

  return (
    <div data-testid="mini-workflow-video"
      className="mt-5 rounded-xl border-2 overflow-hidden relative"
      style={{ borderColor: "rgba(132,204,22,0.5)", backgroundColor: "#0A0518", aspectRatio: "16/9" }}>
      <div className="absolute inset-0">
        {[
          { key: "maps", C: FrameMaps },
          { key: "audit", C: FrameAudit },
          { key: "kanban", C: FrameKanban },
        ].map(({ key, C }, i) => (
          <motion.div key={key}
            initial={false}
            animate={{ opacity: frame === i ? 1 : 0, scale: frame === i ? 1 : 1.04 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="absolute inset-0">
            <C active={frame === i} />
          </motion.div>
        ))}
      </div>
      <div className="absolute bottom-0 inset-x-0 p-2 flex items-center justify-between gap-2"
        style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)" }}>
        <span data-testid="mini-workflow-caption"
          className="text-[10.5px] font-mono uppercase tracking-[0.18em] font-bold"
          style={{ color: "#E5C572" }}>
          {labels[frame]}
        </span>
        <div className="flex gap-1">
          {Array.from({ length: FRAMES }).map((_, i) => (
            <span key={`dot-${i}`} className="w-4 h-1 rounded-full transition-colors"
              style={{ backgroundColor: i === frame ? "#84CC16" : "rgba(255,255,255,0.25)" }} />
          ))}
        </div>
      </div>
      <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
        style={{ backgroundColor: "rgba(0,0,0,0.55)", color: "#10B981" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" /> LIVE LOOP
      </div>
    </div>
  );
}

export function FrameMaps({ active }) {
  return (
    <svg viewBox="0 0 320 180" className="w-full h-full" aria-hidden="true">
      <rect width="320" height="180" fill="#1F2937" />
      <pattern id="mapgrid" width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M20 0H0V20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
      </pattern>
      <rect width="320" height="180" fill="url(#mapgrid)" />
      <path d="M0 110 Q120 95 320 130" stroke="#475569" strokeWidth="6" fill="none" />
      <path d="M180 0 L165 180" stroke="#475569" strokeWidth="6" fill="none" />
      {[
        { x: 60, y: 60, k: "p1" }, { x: 130, y: 95, k: "p2" }, { x: 215, y: 70, k: "p3" },
        { x: 250, y: 130, k: "p4" }, { x: 90, y: 130, k: "p5" },
      ].map((p) => (
        <g key={p.k}>
          <circle cx={p.x} cy={p.y} r="9" fill="#EF4444" />
          <circle cx={p.x} cy={p.y} r="3" fill="#FFFFFF" />
        </g>
      ))}
      <circle cx="215" cy="70" r="14" fill="none" stroke="#84CC16" strokeWidth="2">
        {active && <animate attributeName="r" from="14" to="22" dur="1.6s" repeatCount="indefinite" />}
        {active && <animate attributeName="opacity" from="1" to="0" dur="1.6s" repeatCount="indefinite" />}
      </circle>
      {/* Cursor moving */}
      <g transform="translate(50,150)">
        {active && <animateTransform attributeName="transform" type="translate" from="50 150" to="215 70" dur="1.4s" fill="freeze" begin="0.2s" />}
        <path d="M0 0 L0 12 L4 9 L7 14 L9 13 L6 8 L11 8 Z" fill="#FFFFFF" stroke="#0F172A" strokeWidth="0.6" />
      </g>
      <rect x="12" y="12" width="200" height="22" rx="4" fill="#FFFFFF" />
      <text x="22" y="27" fontSize="10" fontFamily="ui-monospace" fill="#0F172A">roofing miami fl</text>
      <rect x="220" y="12" width="36" height="22" rx="4" fill="#84CC16" />
      <text x="226" y="27" fontSize="9" fontFamily="ui-monospace" fontWeight="700" fill="#0F172A">FIND</text>
    </svg>
  );
}

export function FrameAudit({ active }) {
  return (
    <svg viewBox="0 0 320 180" className="w-full h-full" aria-hidden="true">
      <rect width="320" height="180" fill="#0F172A" />
      <rect x="20" y="20" width="280" height="140" rx="8" fill="#1E293B" stroke="rgba(132,204,22,0.35)" />
      <text x="32" y="40" fontSize="9" fontFamily="ui-monospace" fill="#84CC16" letterSpacing="2">AI AUDIT · GPT</text>
      <text x="32" y="60" fontSize="13" fontFamily="Inter,sans-serif" fontWeight="800" fill="#FFFFFF">Coastal Roofing Co.</text>
      <text x="32" y="76" fontSize="9" fontFamily="ui-monospace" fill="#A1A1AA">coastalroofing.com · ★ 4.8</text>
      <rect x="32" y="92" width="240" height="6" rx="3" fill="rgba(255,255,255,0.08)" />
      <rect x="32" y="92" height="6" rx="3" fill="#84CC16" width={active ? "240" : "20"}>
        {active && <animate attributeName="width" from="20" to="240" dur="1.6s" fill="freeze" />}
      </rect>
      {["Schema markup missing", "Mobile speed 41/100", "No GMB posts 60d"].map((g, i) => (
        <g key={g}>
          <circle cx="40" cy={120 + i * 14} r="3" fill="#F59E0B" />
          <text x="50" y={123 + i * 14} fontSize="9" fontFamily="Inter" fill="#FECACA">{g}</text>
        </g>
      ))}
      <rect x="232" y="115" width="56" height="32" rx="6" fill="#10B981" />
      <text x="260" y="135" textAnchor="middle" fontSize="16" fontFamily="Inter" fontWeight="900" fill="#0F172A">92</text>
    </svg>
  );
}

export function FrameKanban({ active }) {
  return (
    <svg viewBox="0 0 320 180" className="w-full h-full" aria-hidden="true">
      <rect width="320" height="180" fill="#0F172A" />
      {[
        { x: 12, label: "NEW" },
        { x: 110, label: "AUDIT" },
        { x: 208, label: "OUTREACH" },
      ].map((c) => (
        <g key={c.label}>
          <rect x={c.x} y="14" width="100" height="156" rx="6" fill="#1E293B" stroke="rgba(132,204,22,0.18)" />
          <text x={c.x + 8} y="30" fontSize="8" fontFamily="ui-monospace" fontWeight="700" fill="#84CC16" letterSpacing="2">{c.label}</text>
        </g>
      ))}
      {[
        { col: 12, y: 44, k: "c1" },
        { col: 12, y: 76, k: "c2" },
        { col: 110, y: 44, k: "c3" },
      ].map((card) => (
        <rect key={card.k} x={card.col + 8} y={card.y} width="84" height="24" rx="4" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.08)" />
      ))}
      <g transform="translate(0,0)">
        {active && <animateTransform attributeName="transform" type="translate" from="0 -50" to="0 0" dur="0.6s" fill="freeze" />}
        <rect x="216" y="44" width="84" height="36" rx="4" fill="#84CC16" />
        <text x="222" y="58" fontSize="9" fontFamily="Inter" fontWeight="800" fill="#0F172A">Coastal Roofing</text>
        <text x="222" y="72" fontSize="8" fontFamily="ui-monospace" fill="#0F172A">SCORE 92 · HOT</text>
      </g>
    </svg>
  );
}


// ----------------------------------------------------------------------------
// Iter58 — Mobile-first Compare stack. Replaces the unreadable wide table on
// small viewports with one card per feature: AgenteLead value rendered HUGE
// + a horizontal row of 3 small competitor chips below.
// ----------------------------------------------------------------------------

export function CompareMobileStack({ compare, lang, cta }) {
  const ourCol = compare.cols.find((c) => c.highlight) || compare.cols[0];
  const others = compare.cols.filter((c) => c !== ourCol);
  const ourLabel = lang === "pt" ? "Nossa escolha" : lang === "es" ? "Nuestra elección" : "Our pick";
  const ourSubLabel = lang === "pt" ? "Você ganha" : lang === "es" ? "Lo que ganas" : "You get";
  const compLabel = lang === "pt" ? "Concorrentes" : lang === "es" ? "Competencia" : "Competitors";

  const renderValue = (val, big) => {
    if (typeof val === "boolean") {
      return val ? (
        <span data-testid="compare-cell-yes"
          className={`inline-flex items-center justify-center rounded-full ${big ? "w-9 h-9" : "w-5 h-5"}`}
          style={{ backgroundColor: "#10B981", boxShadow: big ? "0 0 16px rgba(16,185,129,0.55)" : "none" }}>
          <Check className={big ? "w-5 h-5 text-white" : "w-3 h-3 text-white"} strokeWidth={3.5} />
        </span>
      ) : (
        <span data-testid="compare-cell-no" className={`inline-flex items-center justify-center rounded-full ${big ? "w-9 h-9" : "w-5 h-5"}`}
          style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
          <span className={`${big ? "text-[18px]" : "text-[12px]"} leading-none`} style={{ color: "rgba(255,255,255,0.4)" }}>—</span>
        </span>
      );
    }
    return (
      <span className={`font-display font-black tabular-nums ${big ? "text-[20px]" : "text-[12px]"}`}
        style={{ color: big ? "#84CC16" : "rgba(255,255,255,0.85)" }}>
        {val}
      </span>
    );
  };

  return (
    <div data-testid="compare-mobile-stack" className="md:hidden mt-10 space-y-4">
      {/* Sticky-friendly recap header */}
      <div className="rounded-xl border-2 px-4 py-3 flex items-center justify-between gap-3"
        style={{ borderColor: "#84CC16", backgroundColor: "rgba(132,204,22,0.10)" }}>
        <div>
          <div className="text-[9.5px] font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "#84CC16" }}>★ {ourLabel}</div>
          <div className="font-display text-[18px] font-black text-white leading-tight">{ourCol.name}</div>
        </div>
        <div className="text-right">
          <div className="text-[9.5px] font-mono uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.55)" }}>{compLabel}</div>
          <div className="text-[12.5px] font-bold" style={{ color: "rgba(255,255,255,0.65)" }}>{others.map((o) => o.name).join(" · ")}</div>
        </div>
      </div>

      {compare.features.map((f) => {
        const ourCell = ourCol.cells[f.k];
        return (
          <div key={f.k} data-testid={`compare-mobile-row-${f.k}`}
            className="rounded-xl border overflow-hidden"
            style={{ borderColor: "rgba(132,204,22,0.22)", backgroundColor: "#1E293B" }}>
            {/* Feature label */}
            <div className="px-4 pt-4 text-[10.5px] font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "rgba(255,255,255,0.55)" }}>
              {f.l}
            </div>

            {/* Hero AgenteLead row */}
            <div className="px-4 pb-4 pt-2 flex items-center gap-3">
              <div className="shrink-0">{renderValue(ourCell, true)}</div>
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: "#84CC16" }}>
                  {ourSubLabel}
                </div>
                <div className="text-[14px] font-bold leading-snug text-white truncate">
                  {f.l}
                </div>
              </div>
            </div>

            {/* Competitor row */}
            <div className="grid grid-cols-3 border-t" style={{ borderColor: "rgba(132,204,22,0.15)" }}>
              {others.map((col) => (
                <div key={col.k} data-testid={`compare-mobile-cell-${f.k}-${col.k}`}
                  className="px-2 py-3 text-center flex flex-col items-center gap-1.5"
                  style={{ borderRight: "1px solid rgba(132,204,22,0.08)" }}>
                  <div className="text-[9.5px] font-mono uppercase tracking-wider truncate w-full" style={{ color: "rgba(255,255,255,0.5)" }}>
                    {col.name}
                  </div>
                  {renderValue(col.cells[f.k], false)}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* CTA repeated to keep conversion velocity */}
      <Link to="/signup" data-testid="compare-mobile-cta"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 font-extrabold text-[13.5px] px-5 py-3.5 rounded-md transition-all"
        style={{ backgroundColor: "#84CC16", color: "#0F172A",
                 boxShadow: "0 12px 40px -14px rgba(132,204,22,0.55)" }}>
        {cta}<ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

